import { Date, DateService, DateTimeService, DateTimeUtils, unitByDateTimePart } from '@mews-framework/datetime';
import { FieldAdapter } from '@mews-framework/form';
import { DateTimeLocalizationInfo, useDateTimeLocalizationInfo, useTimezone } from '@mews-framework/globalization';
import { UnicodeEntity } from '@mews/enums';
import { OptimusTextKey } from '@optimus-web/localization';
import { datePeriods, dateUnitByDateTimePart } from './constants';
import { DatePeriod, DatePeriodType, DateRange, GetDefaultInputValueProps } from './types';

const getDateTimeOffset = (datePeriodType: DatePeriodType) => {
    switch (datePeriodType) {
        case DatePeriodType.Last:
            return -1;
        case DatePeriodType.Next:
            return 1;
        default:
            return 0;
    }
};

const getStartEndDateTime = (datePeriod: DatePeriod, timezone: string) => {
    const today = DateTimeService.startOfDay(DateTimeService.fromDate(DateService.today(timezone)));
    const { type, unit } = datePeriods[datePeriod];
    const dateTimeOffset = getDateTimeOffset(type);
    const dateTime =
        dateTimeOffset === 0 ? today : DateTimeService.add(dateUnitByDateTimePart[unit])(today, dateTimeOffset);

    const startOfDateUnit = DateTimeService.startOf(unitByDateTimePart[unit])(dateTime);
    const endOfDateUnit = DateTimeService.endOf(unitByDateTimePart[unit])(dateTime);

    return {
        endDate: DateTimeUtils.dateTimeUtcStringToDateISOString(DateTimeService.toUtc(endOfDateUnit).value, timezone),
        startDate: DateTimeUtils.dateTimeUtcStringToDateISOString(
            DateTimeService.toUtc(startOfDateUnit).value,
            timezone
        ),
    };
};

export const getDateRangeByPeriod = (datePeriod: DatePeriod, timezone: string) => ({
    endDate: getStartEndDateTime(datePeriod, timezone).endDate,
    startDate: getStartEndDateTime(datePeriod, timezone).startDate,
});

export const useGetDefaultInputValue = ({
    dateRange,
    mode,
    transform,
}: GetDefaultInputValueProps): OptimusTextKey | string => {
    const timezone = useTimezone();
    const localizationInfo = useDateTimeLocalizationInfo();
    const { datePeriod } = dateRange;

    if (datePeriod === DatePeriod.Custom) {
        return getTransformedDateRangeOutput({
            endDate: dateRange.endDate,
            localizationInfo,
            mode,
            startDate: dateRange.startDate,
            transform,
            timezone,
        });
    }

    return datePeriods[datePeriod].label;
};

export const useGetDefaultCustomRangeState = (mode: 'iso' | 'utc', dateRange: DateRange) => {
    const localizationInfo = useDateTimeLocalizationInfo();
    const timezone = useTimezone();

    if (dateRange.datePeriod !== DatePeriod.Custom) {
        return {
            endDate: undefined,
            startDate: undefined,
        };
    }

    const { endDate: endDateString, startDate: startDateString } = dateRange;

    let endDate: Date | undefined;
    let startDate: Date | undefined;

    if (mode === 'iso') {
        endDate = DateService.fromString(
            DateTimeUtils.dateIsoStringToDateString(endDateString, localizationInfo),
            localizationInfo
        );
        startDate = DateService.fromString(
            DateTimeUtils.dateIsoStringToDateString(startDateString, localizationInfo),
            localizationInfo
        );
    } else if (mode === 'utc') {
        endDate = DateService.fromString(
            DateTimeUtils.dateTimeUtcStringToDateString(endDateString, localizationInfo, timezone),
            localizationInfo
        );
        startDate = DateService.fromString(
            DateTimeUtils.dateTimeUtcStringToDateString(startDateString, localizationInfo, timezone),
            localizationInfo
        );
    }

    if (!endDate || !startDate) {
        return {
            endDate: undefined,
            startDate: undefined,
        };
    }

    return {
        endDate: DateTimeUtils.dateToStructuredDate(endDate),
        startDate: DateTimeUtils.dateToStructuredDate(startDate),
    };
};

interface TransformedDateRangeOutputProps {
    endDate: string;
    localizationInfo: DateTimeLocalizationInfo;
    mode: 'iso' | 'utc';
    startDate: string;
    timezone: string;
    transform: FieldAdapter;
}

export const getTransformedDateRangeOutput = ({
    endDate,
    localizationInfo,
    mode,
    startDate,
    timezone,
    transform,
}: TransformedDateRangeOutputProps) => {
    if (!endDate || !startDate) {
        return '';
    }

    let endDateString: string | undefined;
    let startDateString: string | undefined;

    if (mode === 'iso') {
        endDateString = DateTimeUtils.dateIsoStringToDateString(endDate, localizationInfo);
        startDateString = DateTimeUtils.dateIsoStringToDateString(startDate, localizationInfo);
    } else if (mode === 'utc') {
        endDateString = DateTimeUtils.dateTimeUtcStringToDateString(endDate, localizationInfo, timezone);
        startDateString = DateTimeUtils.dateTimeUtcStringToDateString(startDate, localizationInfo, timezone);
    }

    const transformedEndDateOutput = transform?.output ? transform.output(endDateString) : endDateString;
    const transformedStartDateOutput = transform?.output ? transform.output(startDateString) : startDateString;

    return `${String(transformedStartDateOutput)} ${UnicodeEntity.Dash} ${String(transformedEndDateOutput)}`;
};
